import clsx from 'clsx';
import { Suspense, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DetailPageHeader } from '@/components/detailPageHeader/detailPageHeader';
import { Loader } from '@/components/loader/loader';
import { TabItem, Tabs } from '@/components/tabs/tabs';
import { useSource, useSourcePreview } from '@/hooks/queries/useSources';
import { SOURCE_TYPE_DISPLAY_NAMES } from '@/types/evals';
import { parseParams } from '@/utils/searchParams';
import { getResourceIcon } from '@/utils/sourceUtils';

import { SourceDetailsTab } from './_sourceDetailsTab/SourceDetailsTab';
import { VersionConfigurationTab } from './_versionConfigurationTab/VersionConfigurationTab';
import styles from './page.module.scss';

type SearchParams = {
  sid: string;
  version: string;
  tab: string;
};

export type SourceTab = 'version' | 'details';

const SOURCE_TAB_VALUES: readonly SourceTab[] = [ 'version', 'details' ] as const;

function isSourceTab( value: string | null | undefined ): value is SourceTab {
  return SOURCE_TAB_VALUES.includes( value as SourceTab );
}

const SOURCE_TABS: TabItem<SourceTab>[] = [
  { value: 'version', label: 'Version Configuration', 'data-testid': 'source-tab-version' },
  { value: 'details', label: 'Source Details', 'data-testid': 'source-tab-details' }
];

function parseVersionParam( value: string ): number | 'latest' {
  if ( value === '' || value === 'latest' ) return 'latest';
  const parsed = Number.parseInt( value, 10 );
  return Number.isFinite( parsed ) ? parsed : 'latest';
}

function EvaluationSourcePage() {
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    sid: qSourceId = '',
    version: qVersion = '',
    tab: qTab = ''
  } = parseParams<SearchParams>( searchParams );

  const activeTab: SourceTab = isSourceTab( qTab ) ? qTab : 'version';
  const versionForQuery = parseVersionParam( qVersion );

  const sourceQuery = useSource( qSourceId, versionForQuery );
  const previewQuery = useSourcePreview( qSourceId, versionForQuery );

  const goToTab = useCallback( ( next: SourceTab ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'tab', next );
      return params;
    } );
  }, [ setSearchParams ] );

  const updateVersion = useCallback( ( newVersion: number ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'version', newVersion.toString() );
      return params;
    } );
  }, [ setSearchParams ] );

  const onSourceChanged = useCallback( () => {
    sourceQuery.refetch();
    previewQuery.refetch();
  }, [ sourceQuery, previewQuery ] );

  if ( !qSourceId ) {
    return (
      <div className='page'>
        <div>No source specified</div>
      </div>
    );
  }

  // A failed fetch leaves isLoading false but data undefined, so the loading
  // guard below would otherwise spin forever. Surface the error and let the
  // user retry instead.
  if ( sourceQuery.isError ) {
    return (
      <div className='page'>
        <div className={ clsx( 'layout__col', 'pad' ) } data-testid="source-load-error">
          <div>Unable to load source.</div>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onSourceChanged }
            data-testid="source-load-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if ( sourceQuery.isLoading || !sourceQuery.data ) {
    return (
      <div className='page'>
        <Loader />
      </div>
    );
  }

  const source = sourceQuery.data;

  return (
    <div className='page'>
      <DetailPageHeader
        backTo="/evaluations/sources"
        backLabel="Back to Sources"
        icon={ getResourceIcon( source ) }
        title={ source.name }
        subtitle={ `Source (${ SOURCE_TYPE_DISPLAY_NAMES[source.source_type] })` }
      />

      <Tabs
        items={ SOURCE_TABS }
        active={ activeTab }
        onChange={ goToTab }
        variant="plain"
        data-testid="source-tabs"
      />

      <div className={ clsx( 'layout__col', styles.tabContent ) }>
        { activeTab === 'version' && (
          <VersionConfigurationTab
            source={ source }
            preview={ previewQuery.data }
            previewIsLoading={ previewQuery.isLoading }
            onSourceChanged={ onSourceChanged }
            onVersionChange={ updateVersion }
          />
        ) }

        { activeTab === 'details' && (
          <SourceDetailsTab source={ source } />
        ) }
      </div>
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ToastContainer />
      <EvaluationSourcePage />
    </Suspense>
  );
}
